import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  contractualTerms,
  type ContractualTerm,
  type OperationDetail,
  type ParticularClauseChoice,
} from '@/shared/api/resources';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Modal } from '@/shared/ui/Modal';
import { RichTextView } from '@/shared/ui/RichTextEditor';
import { useToast } from '@/shared/ui/useToast';

import { ContractualTermForm } from './ContractualTermsEditor';
import {
  chaveDosTermos,
  usePermissoesDosTermos,
  useTermosDaCotacao,
} from './termosDaCotacao';

/**
 * Secao "Clausulas particulares" do Resumo da cotacao (COT-063 a COT-068): as
 * clausulas escolhidas e o botao "Adicionar / Alterar Clausulas Particulares", que
 * abre o dialogo "Condicoes Particulares" do OnPoint.
 *
 * As marcacoes "Corretor pode inserir?" e "Automatica Subscricao?" aparecem e nao
 * decidem: a clausula nao automatica ainda nao vai para a subscricao (SUB-014).
 */
export function ParticularClausesSection({ operacao }: { operacao: OperationDetail }) {
  const consulta = useTermosDaCotacao(operacao.id);
  const pode = usePermissoesDosTermos(operacao, consulta.data);
  const [escolhendo, setEscolhendo] = useState(false);
  const [vendo, setVendo] = useState<ContractualTerm | null>(null);

  const clausulas = (consulta.data?.items ?? []).filter(
    (termo) => termo.kind === 'clausula_particular',
  );
  const aberta = vendo ? clausulas.find((termo) => termo.id === vendo.id) : undefined;

  return (
    <Card>
      <CardHeader
        title="Clausulas particulares"
        description="Clausulas escolhidas do cadastro da modalidade, com o texto e a versao do dia da escolha."
        action={
          pode.clausulas ? (
            <Button variant="secondary" onClick={() => setEscolhendo(true)}>
              Adicionar / Alterar Clausulas Particulares
            </Button>
          ) : null
        }
      />
      <CardBody>
        {consulta.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar as clausulas">
            {consulta.error.message}
          </Alert>
        ) : consulta.isPending ? (
          <p className="text-sm text-ink-500">Carregando as clausulas.</p>
        ) : clausulas.length === 0 ? (
          <EmptyState
            icon="stamp"
            title="Nenhuma clausula particular"
            description="A cotacao nao tem clausula particular. As opcoes sao as condicoes particulares ativas da modalidade."
          />
        ) : (
          <ul className="flex flex-col divide-y divide-line">
            {clausulas.map((termo) => (
              <li
                key={termo.id}
                className="flex flex-wrap items-center justify-between gap-3 py-2.5"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink-900">{termo.name}</p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {termo.template_version ? (
                      <Badge tone="neutral">Versao {termo.template_version}</Badge>
                    ) : null}
                    {termo.free_text ? <Badge tone="info">Em branco</Badge> : null}
                    {termo.particular_clause?.broker_can_select ? (
                      <Badge tone="neutral">Corretor pode inserir</Badge>
                    ) : null}
                    {termo.particular_clause?.automatic_on_underwriting ? (
                      <Badge tone="neutral">Automatica na subscricao</Badge>
                    ) : null}
                    {termo.particular_clause && !termo.particular_clause.active ? (
                      <Badge tone="warning">Inativa no cadastro</Badge>
                    ) : null}
                    {!termo.enabled ? (
                      <Badge tone="neutral">Fora da minuta</Badge>
                    ) : null}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  aria-label={`Abrir a clausula ${termo.name}`}
                  onClick={() => setVendo(termo)}
                >
                  {pode.textos ? 'Abrir e editar' : 'Ver texto'}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardBody>

      {escolhendo ? (
        <ParticularClausesDialog
          operationId={operacao.id}
          soDaCorretora={pode.soDaCorretora}
          onClose={() => setEscolhendo(false)}
        />
      ) : null}

      {aberta ? (
        <Modal
          open
          onClose={() => setVendo(null)}
          title={`Clausula particular: ${aberta.name}`}
          description="O texto vem do cadastro da modalidade; a clausula em branco e escrita aqui."
          size="lg"
        >
          {pode.textos ? (
            <ContractualTermForm
              operationId={operacao.id}
              termo={aberta}
              podeEditar={pode.textos}
              bloco={pode.doBloco(aberta)}
            />
          ) : (
            <RichTextView html={aberta.body} />
          )}
        </Modal>
      ) : null}
    </Card>
  );
}

/**
 * Dialogo "Condicoes Particulares" (COT-065 a COT-067): a lista multipla com os chips
 * das escolhidas, "Selecionar todas", "Cancelar" e "Prosseguir". As opcoes vem do
 * servidor: as clausulas ativas da modalidade, e a de um tomador so na cotacao dele.
 */
export function ParticularClausesDialog({
  operationId,
  soDaCorretora = false,
  onClose,
}: {
  operationId: string;
  /**
   * A corretora na propria cotacao: a clausula escolhida pela seguradora fica
   * (achado A2 da revisao das ondas 5 e 6). O servidor recusa tira-la.
   */
  soDaCorretora?: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [escolhidas, setEscolhidas] = useState<string[] | null>(null);

  const opcoes = useQuery({
    queryKey: ['operations', operationId, 'particular-clauses'],
    queryFn: ({ signal }) => contractualTerms.clauseChoices(operationId, signal),
  });

  const gravar = useMutation({
    mutationFn: (ids: string[]) => contractualTerms.replaceClauses(operationId, ids),
    onSuccess: (termos) => {
      toast.success('Clausulas particulares gravadas.');
      queryClient.setQueryData(chaveDosTermos(operationId), termos);
      void queryClient.invalidateQueries({
        queryKey: ['operations', operationId],
        exact: true,
      });
      onClose();
    },
  });

  const lista: ParticularClauseChoice[] = opcoes.data ?? [];
  const marcadas =
    escolhidas ?? lista.filter((opcao) => opcao.selected).map((opcao) => opcao.id);
  const todas = lista.length > 0 && marcadas.length === lista.length;
  // A clausula da seguradora ja escolhida nao sai pela corretora.
  const presas = soDaCorretora
    ? lista
        .filter((opcao) => opcao.selected && !opcao.broker_can_select)
        .map((opcao) => opcao.id)
    : [];

  function alternar(id: string) {
    if (presas.includes(id)) return;
    setEscolhidas(
      marcadas.includes(id)
        ? marcadas.filter((item) => item !== id)
        : [...marcadas, id],
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Condicoes Particulares"
      description="Escolha as clausulas da cotacao. A clausula nova entra com o texto e a versao atuais do cadastro; a que ja estava nao muda."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            loading={gravar.isPending}
            disabled={opcoes.isPending || opcoes.isError}
            onClick={() => gravar.mutate(marcadas)}
          >
            Prosseguir
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {gravar.error ? (
          <Alert tone="danger" title="Nao foi possivel gravar as clausulas">
            {gravar.error.message}
          </Alert>
        ) : null}

        {opcoes.isPending ? (
          <p className="text-sm text-ink-500">Carregando as clausulas da modalidade.</p>
        ) : opcoes.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar as clausulas">
            {opcoes.error.message}
          </Alert>
        ) : lista.length === 0 ? (
          <Alert tone="info" title="Nenhuma condicao particular ativa">
            Cadastre as condicoes particulares na modalidade, em Parametrizacao.
          </Alert>
        ) : (
          <>
            <div
              className="flex min-h-9 flex-wrap gap-1.5 rounded-md border border-line px-2 py-1.5"
              aria-label="Clausulas escolhidas"
            >
              {marcadas.length === 0 ? (
                <span className="text-xs text-ink-400">Nenhuma clausula escolhida</span>
              ) : (
                lista
                  .filter((opcao) => marcadas.includes(opcao.id))
                  .map((opcao) => (
                    <span
                      key={opcao.id}
                      className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-xs text-brand-700"
                    >
                      {opcao.name}
                      {presas.includes(opcao.id) ? null : (
                        <button
                          type="button"
                          aria-label={`Tirar ${opcao.name}`}
                          className="text-brand-700 hover:text-brand-900"
                          onClick={() => alternar(opcao.id)}
                        >
                          x
                        </button>
                      )}
                    </span>
                  ))
              )}
            </div>

            <label className="flex cursor-pointer items-center gap-2 border-b border-line pb-2 text-sm font-medium text-ink-800">
              <input
                type="checkbox"
                checked={todas}
                onChange={(event) =>
                  setEscolhidas(
                    event.target.checked ? lista.map((opcao) => opcao.id) : presas,
                  )
                }
                className="size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
              />
              Selecionar todas
            </label>

            <ul className="flex max-h-[50vh] flex-col gap-1 overflow-auto">
              {lista.map((opcao) => (
                <li key={opcao.id}>
                  <label className="flex cursor-pointer items-start gap-2 rounded px-1 py-1.5 text-sm text-ink-800 hover:bg-ink-50">
                    <input
                      type="checkbox"
                      checked={marcadas.includes(opcao.id)}
                      disabled={presas.includes(opcao.id)}
                      onChange={() => alternar(opcao.id)}
                      className="mt-0.5 size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
                    />
                    <span className="min-w-0">
                      {opcao.name}
                      <span className="mt-0.5 flex flex-wrap gap-1">
                        {opcao.free_text ? <Badge tone="info">Em branco</Badge> : null}
                        {opcao.policyholder_restricted ? (
                          <Badge tone="neutral">Do tomador</Badge>
                        ) : null}
                        {opcao.broker_can_select ? (
                          <Badge tone="neutral">Corretor pode inserir</Badge>
                        ) : null}
                        {!opcao.active ? <Badge tone="warning">Inativa</Badge> : null}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </Modal>
  );
}
